import React, { useState, useEffect } from "react";
import { getPeriodSummary, PeriodSummaryResponse } from "../../../../Rest_api/RestApi";
import { BarChart3, Activity, Zap } from "lucide-react";

interface SimulationSummaryTilesProps {
    availableDates: any[];
    selectedExchanges: string[];
    selectedMarkets: string[];
    selectedFilterDate: any[];
    setSelectedFilterDate: (dates: any[]) => void;
}

const SimulationSummaryTiles: React.FC<SimulationSummaryTilesProps> = ({
    availableDates,
    selectedExchanges,
    selectedMarkets,
    selectedFilterDate,
    setSelectedFilterDate,
}) => {
    const [data, setData] = useState<PeriodSummaryResponse | null>(null);
    const [loading, setLoading] = useState(false);

    // 1. Sync selected dates with availableDates (remove stale selections)
    useEffect(() => {
        if (availableDates.length > 0) {
            const validSelectedDates = selectedFilterDate.filter((selected) =>
                availableDates.some((available) => available.value === selected.value)
            );

            if (validSelectedDates.length === 0) {
                // If no valid selection or selection cleared, default to the first available date
                setSelectedFilterDate([availableDates[0]]);
            } else if (validSelectedDates.length !== selectedFilterDate.length) {
                // If some were invalid, update state
                setSelectedFilterDate(validSelectedDates);
            }
        } else {
            // If no available dates, clear selection
            if (selectedFilterDate.length > 0) setSelectedFilterDate([]);
        }
    }, [availableDates]);

    // 2. Fetch data whenever selectedFilterDate (or filters) change
    useEffect(() => {
        const fetchData = async () => {
            if (selectedFilterDate.length === 0) {
                setData(null);
                return;
            }

            setLoading(true);
            try {
                // Use the FIRST selected date range (since isMany=false)
                const dateOption = selectedFilterDate[0];
                const dateRange = dateOption.value.split(",");
                const startDate = dateRange[0];
                const endDate = dateRange[1] || startDate;

                const result = await getPeriodSummary({
                    startDate,
                    endDate,
                    exchange: selectedExchanges.join(","),
                    product: selectedMarkets.join(","),
                });
                setData(result);
            } catch (error) {
                console.error("Error fetching summary tiles data", error);
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedFilterDate, selectedExchanges, selectedMarkets]);

    const formatValue = (val: number | undefined) => {
        if (val === undefined || val === null) return "-";
        return val.toLocaleString("en-IN", { maximumFractionDigits: 2 });
    };

    const tiles = [
        {
            label: "Weighted Avg MCP",
            value: data ? `${formatValue(data.weighted_avg_mcp)}` : "-",
            icon: <Zap className="w-4 h-4 text-white" />,
            bgColor: "bg-[#FFF6E9]", // Light Orange
            iconBg: "bg-[#F97316]", // Orange
            unit: "Rs/MWh",
        },
        {
            label: "Peak Demand",
            value: data ? `${formatValue(data.peak_demand)}` : "-",
            icon: <Activity className="w-4 h-4 text-white" />,
            bgColor: "bg-[#F3E8FF]", // Light Purple
            iconBg: "bg-[#A855F7]", // Purple
            unit: "MW",
        },
        {
            label: "All India Demand",
            value: data ? `${formatValue(data.all_india_demand)}` : "-",
            icon: <BarChart3 className="w-4 h-4 text-white" />,
            bgColor: "bg-[#ECFDF5]", // Light Green
            iconBg: "bg-[#22C55E]", // Green
            unit: "MU",
        },
        {
            label: "All India Generation",
            value: data ? `${formatValue(data.all_india_generation)}` : "-",
            icon: <BarChart3 className="w-4 h-4 text-white" />,
            bgColor: "bg-[#EFF6FF]", // Light Blue
            iconBg: "bg-[#3B82F6]", // Blue
            unit: "MU",
        },
    ];

    return (
      <div className="flex flex-row gap-2 w-full items-stretch">
        {tiles.map((tile, index) => (
          <div
            key={index}
            className={`${tile.bgColor} flex-1 rounded-xl px-2 py-1.5 flex items-center justify-between border border-gray-200 shadow-sm hover:border-gray-200 transition-all `}
          >
            <div className="flex items-center gap-3 w-full">
              <div
                className={`${tile.iconBg} p-1 rounded-lg shadow-sm flex items-center justify-center`}
              >
                {tile.icon}
              </div>
              <div className="flex flex-row items-center gap-2 flex-1 min-w-0 justify-between">
                <span className="text-xs font-medium text-gray-500 tracking-normal leading-tight truncate">
                  {tile.label}
                </span>
                <div className="flex flex-row items-baseline gap-2 mt-0.5">
                  {loading ? (
                    <div className=" w-16 bg-gray-200/50 animate-pulse rounded"></div>
                  ) : (
                    <span className="text-xs font-semibold text-[#525062] leading-tight">
                      {tile.value}{" "}
                      <span className="text-[8px] font-semibold text-[#525062] leading-tight">
                        {tile.unit}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
};

export default SimulationSummaryTiles;
